import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import {
  PopoverMenu,
  PopoverMenuButton,
  PopoverMenuSection,
  PopoverMenuItems,
  PopoverMenuItem,
  PopoverMenuItemButton,
} from 'design-system/src/components/PopoverMenu/PopoverMenu';
import { emptyTableCellCopy } from 'design-system/src/sharedCopy';
import HorizontalMenu from 'design-system/src/assets/icons/horizontalMenu.svg?react';
import CloseModalIcon from 'design-system/src/assets/icons/closeModal.svg?react';
import { SearchField } from '../Input';
import { TableNullState, TableNullStateType } from '../TableNullState/TableNullState';
import { client } from '../../api';
import { createTrackingProps } from 'shared-utils/analytics';
import AddAddressToOfficeModal from './AddAddressToOfficeModal';
import AddOfficeModal from './AddOfficeModal';
import AddUnmatchedAddressAsOfficeModal from './AddUnmatchedAddressAsOfficeModal';
import EditOfficeModal from './EditOfficeModal';
import RemoveOfficeModal from './RemoveOfficeModal';
import RemoveUnmatchedAddressModal from './RemoveUnmatchedAddressesModal';
import { type OfficeDetail, type OfficeGroup } from './types';
import { officeGroupKey, splitAddress } from './utils';

type UnmatchedAddress = {
  address: string;
  office_id: string | null;
};

type OfficeGroupCardProps = {
  /** The office group to display */
  group: OfficeGroup;
  /** Callback when the user wants to edit the office */
  onEdit: () => void;
  /** Callback when the user wants to add an address to the office */
  onAddAddress: () => void;
  /** Callback when the user wants to remove the office */
  onRemove: () => void;
};

const OfficeGroupCard: React.FC<OfficeGroupCardProps> = ({ group, onEdit, onAddAddress, onRemove }) => (
  <div className="flex gap-2 rounded-md border border-solid border-grayscale-100 bg-white p-2">
    <div className="flex flex-1 flex-col gap-0.5 p-2">
      <Typography styledAs="bodySmallDMSans" weight={600}>
        {group.name}
      </Typography>
      {group.allOffices.length > 1 ? (
        <>
          <Typography styledAs="bodyExtraSmallDMSans">{splitAddress(group.displayOffice.address).street}</Typography>
          <Typography styledAs="bodyExtraSmallDMSans">+{group.allOffices.length - 1} more</Typography>
        </>
      ) : (
        <>
          <Typography styledAs="bodyExtraSmallDMSans">{splitAddress(group.displayOffice.address).street}</Typography>
          {splitAddress(group.displayOffice.address).cityLine && (
            <Typography styledAs="bodyExtraSmallDMSans">
              {splitAddress(group.displayOffice.address).cityLine}
            </Typography>
          )}
        </>
      )}
      <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
        TIN: {group.tin || emptyTableCellCopy}
      </Typography>
    </div>
    <PopoverMenu>
      <PopoverMenuButton className="flex size-9 cursor-pointer items-center justify-center rounded-md border-none bg-transparent hover:bg-grayscale-50 active:bg-grayscale-100">
        <HorizontalMenu title="actions" />
      </PopoverMenuButton>
      <PopoverMenuItems width="200px">
        <PopoverMenuSection>
          <PopoverMenuItem>
            {(itemProps) => (
              <PopoverMenuItemButton
                {...itemProps}
                onClick={onEdit}
                {...createTrackingProps({ elementName: 'Offices: Edit Office' })}
              >
                Edit office
              </PopoverMenuItemButton>
            )}
          </PopoverMenuItem>
          <PopoverMenuItem>
            {(itemProps) => (
              <PopoverMenuItemButton
                {...itemProps}
                onClick={onAddAddress}
                {...createTrackingProps({ elementName: 'Offices: Add Address' })}
              >
                Add an address
              </PopoverMenuItemButton>
            )}
          </PopoverMenuItem>
          <PopoverMenuItem>
            {(itemProps) => (
              <PopoverMenuItemButton
                {...itemProps}
                onClick={onRemove}
                {...createTrackingProps({ elementName: 'Offices: Remove Office' })}
              >
                Remove office
              </PopoverMenuItemButton>
            )}
          </PopoverMenuItem>
        </PopoverMenuSection>
      </PopoverMenuItems>
    </PopoverMenu>
  </div>
);

/**
 * Component for managing offices in settings.
 */
const Offices: React.FC = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [assigningAddress, setAssigningAddress] = useState<UnmatchedAddress | null>(null);
  const [editingOffices, setEditingOffices] = useState<OfficeDetail[] | null>(null);
  const [removingOffices, setRemovingOffices] = useState<OfficeDetail[] | null>(null);
  const [addingAddressToOffice, setAddingAddressToOffice] = useState<OfficeDetail | null>(null);
  const [removingAddress, setRemovingAddress] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: officesData,
    error: officesError,
    isPending: officesLoading,
  } = useQuery<{ offices: OfficeDetail[] }>({
    queryKey: ['offices-v2'],
    queryFn: () => client.get('/settings/offices/v2').then((res) => res.data),
  });

  const { data: unmatchedData } = useQuery<{ addresses: UnmatchedAddress[] }>({
    queryKey: ['unmatched-addresses'],
    queryFn: () => client.get('/settings/offices/unmatched-addresses').then((res) => res.data),
  });

  const offices = useMemo(() => officesData?.offices ?? [], [officesData]);
  const unmatchedAddresses = useMemo(() => unmatchedData?.addresses ?? [], [unmatchedData]);

  const officeGroups = useMemo(() => {
    const sortedOffices = [...offices].sort((a, b) => a.id.localeCompare(b.id));
    const groups = new Map<string, OfficeGroup>();
    for (const office of sortedOffices) {
      const key = officeGroupKey(office);
      const existing = groups.get(key);
      if (existing) {
        existing.allOffices.push(office);
      } else {
        groups.set(key, {
          name: office.name,
          tin: office.tin,
          displayOffice: office,
          allOffices: [office],
        });
      }
    }
    return Array.from(groups.values());
  }, [offices]);

  const filteredUnmatchedAddresses = useMemo(() => {
    if (!searchQuery.trim()) return unmatchedAddresses;
    const query = searchQuery.toLowerCase();
    return unmatchedAddresses.filter((a) => a.address.toLowerCase().includes(query));
  }, [unmatchedAddresses, searchQuery]);

  if (officesLoading) {
    return <LoadingSpinner height="200px" />;
  } else if (officesError || !officesData) {
    return <PageLoadError />;
  }

  const isEmpty = offices.length === 0 && unmatchedAddresses.length === 0;

  return (
    <>
      {isEmpty ? (
        <TableNullState nullStateType={TableNullStateType.NO_OFFICES} onAction={() => setShowAddModal(true)} />
      ) : (
        <div className="flex flex-col gap-12 py-5">
          {/* Offices section */}
          <div className="flex flex-col gap-3">
            <ButtonRow align={Alignment.SpaceBetween}>
              <div className="flex flex-col">
                <Typography styledAs="bodyDMSans" weight={600}>
                  Offices
                </Typography>
                <Typography styledAs="bodySmallDMSans">
                  Manage related addresses and formalize tax numbers using offices.
                </Typography>
              </div>
              <Button
                size="sm"
                onClick={() => setShowAddModal(true)}
                {...createTrackingProps({ elementName: 'Offices: Add Office Button' })}
              >
                Add office
              </Button>
            </ButtonRow>

            {officeGroups.length === 0 ? (
              <div className="flex min-h-16 items-center justify-center rounded-md border border-solid border-grayscale-100 bg-white px-3">
                <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
                  No offices have been added
                </Typography>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {officeGroups.map((group) => (
                  <OfficeGroupCard
                    key={officeGroupKey(group)}
                    group={group}
                    onEdit={() => setEditingOffices(group.allOffices)}
                    onAddAddress={() => setAddingAddressToOffice(group.displayOffice)}
                    onRemove={() => setRemovingOffices(group.allOffices)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Unmatched addresses section */}
          {unmatchedAddresses.length > 0 && (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col">
                <Typography styledAs="bodyDMSans" weight={600}>
                  Unmatched addresses
                </Typography>
                <Typography styledAs="bodySmallDMSans">
                  These addresses were found in your prescription data but don't match any existing office. Add them to
                  an existing office or create a new one.
                </Typography>
              </div>

              <SearchField
                label="Search by address"
                hideLabel
                placeholder="Search by address"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                className="w-[353px]"
                inputSize="sm"
                {...createTrackingProps({ elementName: 'Offices: Search Unmatched Addresses' })}
              />

              <div className="flex flex-col">
                {filteredUnmatchedAddresses.map((item) => (
                  <div
                    key={item.address}
                    className="flex items-center rounded-md py-2.5"
                    style={{ borderBottom: '1px solid var(--grayscale-100)' }}
                  >
                    <div className="flex flex-1 items-center gap-3">
                      <button
                        type="button"
                        className="flex shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0"
                        onClick={() => setRemovingAddress(item.address)}
                        aria-label={`Remove ${item.address}`}
                        {...createTrackingProps({ elementName: 'Offices: Remove Unmatched Address' })}
                      >
                        <CloseModalIcon className="size-5" />
                      </button>
                      <div className="flex flex-1 items-center justify-between">
                        <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
                          {item.address}
                        </Typography>
                        <Button
                          size="sm"
                          variant="tertiary"
                          onClick={() => setAssigningAddress(item)}
                          className="border-grayscale-400"
                          {...createTrackingProps({ elementName: 'Offices: Add Unmatched Address as Office' })}
                        >
                          Add as office
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showAddModal && <AddOfficeModal onClose={() => setShowAddModal(false)} />}
      {editingOffices && <EditOfficeModal onClose={() => setEditingOffices(null)} matchedAddresses={editingOffices} />}
      {removingOffices && <RemoveOfficeModal onClose={() => setRemovingOffices(null)} offices={removingOffices} />}
      {addingAddressToOffice && (
        <AddAddressToOfficeModal onClose={() => setAddingAddressToOffice(null)} office={addingAddressToOffice} />
      )}
      {assigningAddress && (
        <AddUnmatchedAddressAsOfficeModal
          onClose={() => setAssigningAddress(null)}
          address={assigningAddress.address}
          officeId={assigningAddress.office_id}
          officeGroups={officeGroups}
        />
      )}
      {removingAddress && (
        <RemoveUnmatchedAddressModal onClose={() => setRemovingAddress(null)} address={removingAddress} />
      )}
    </>
  );
};

export default Offices;
